import {
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  Box,
  Badge,
  Button,
  Chip,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import axios from "axios";
import { deleteData } from "const/axios";
import { RefreshData } from "components/helper";
import DeleteModal from "components/Modals/DeleteModal";
import { API_URL, currency } from "const/constants";
import useKothar from "context/useKothar";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import { AiFillDelete, AiFillEdit } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import { getNotificationDestination } from "utils/notificationNavigation";
import { toast } from "react-toastify";
import GenerateInvoice from "./GenerateInvoice";
import AddAccountsModal from "./AddAccountsModal";
import BulkProviderInvoiceModal from "./BulkProviderInvoiceModal";
import AccountMasterGroupManager from "./AccountMasterGroupManager";
import AccountVerificationRequests from "./AccountVerificationRequests";
import ModulePaymentVerificationRequests from "./ModulePaymentVerificationRequests";
import AccountNotificationsPopover from "./AccountNotificationsPopover";
import { P } from "const/permissions";
import { hasPermissionKey } from "utils/permissionUtils";
import { getMrtBodyRowSx, getMrtTableThemeProps } from "theme/mrtTableTheme";
import { getThemedTabsSx } from "theme/tabsTheme";
import { getAccountAreaNotifications } from "utils/accountNotificationUtils";

const ACCOUNT_TAB_OPTIONS = [
  { label: "Client", value: "client" },
  { label: "Provider", value: "provider" },
];

const Accounts = ({ color = "light" }) => {
  const navigate = useNavigate();
  const userDetail = JSON.parse(localStorage.getItem("userDetail") || "null");
  const roleValue = userDetail?.type ?? userDetail?.role;
  const userRole = typeof roleValue === "string" ? roleValue : roleValue?.roleType;
  const canViewPaymentVerifications = hasPermissionKey(P.ACCOUNTS.PAYMENT_READ);
  const canViewTermVerificationRequests = hasPermissionKey(P.ACCOUNTS.VERIFICATION_READ);
  const accountTabOptions = useMemo(() => {
    const tabs = [...ACCOUNT_TAB_OPTIONS];
    if (canViewPaymentVerifications) {
      tabs.push({ label: "Payment Verifications", value: "payment-verifications" });
    }
    // Hidden for now
    if (false && canViewTermVerificationRequests) {
      tabs.push({ label: "Verification Requests", value: "verification" });
    }
    return tabs;
  }, [canViewPaymentVerifications, canViewTermVerificationRequests]);
  const [openConfirmationModal, setOpenConfirmationModal] = useState({});
  const [isGenerateInvoiceOpen, setIsGenerateInvoiceOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [
    {
      accountsList,
      accountsTotalItems,
      providerList,
      providerTotalItems,
      providerSummary,
      providerPagination,
      isProviderListLoading,
      isAccountsListLoading,
      accountPagination,
      notificationsListForAccounts,
      notificationPaginationForAccounts,
      isNotificationLoadingForAccounts,
      unreadAccountNotificationsCount,
    },
    {
      refetchAccountList,
      refetchProvider,
      setProviderPagination,
      refetchNotificationsForAccounts,
      refetchNotificationDataForAccounts,
      setAccountPagination,
      setIsAccountsPageActive,
    },
  ] = useKothar();
  const [openAddAccountModal, setOpenAddAccountModal] = useState(false);
  const [openBulkProviderInvoice, setOpenBulkProviderInvoice] = useState(false);
  const [openGroupManager, setOpenGroupManager] = useState(false);
  const [accountGroups, setAccountGroups] = useState([]);
  const [selectedGroupFilter, setSelectedGroupFilter] = useState(null);
  const [verificationCount, setVerificationCount] = useState(0);
  const [paymentVerificationCount, setPaymentVerificationCount] = useState(0);
  const [value, setValue] = useState(0);
  const [accountsNotifAnchorEl, setAccountsNotifAnchorEl] = useState(null);
  const [markingReadId, setMarkingReadId] = useState(null);
  const initialLoadDoneRef = useRef(false);
  const providerLoadDoneRef = useRef(false);
  const accountPaginationMountRef = useRef(true);
  const providerPaginationMountRef = useRef(true);

  const accountNotifications = useMemo(
    () => getAccountAreaNotifications(notificationsListForAccounts),
    [notificationsListForAccounts]
  );

  useEffect(() => {
    setIsAccountsPageActive(true);
    return () => setIsAccountsPageActive(false);
  }, []);

  useEffect(() => {
    const tab = localStorage.getItem("accountTab");
    if (tab) {
      const idx = accountTabOptions.findIndex((item) => item.value === tab);
      if (idx >= 0) {
        setValue(idx);
      }
    }
  }, [accountTabOptions]);

  useEffect(() => {
    if (initialLoadDoneRef.current) return;
    initialLoadDoneRef.current = true;
    refetchAccountList(accountPagination);
    fetchAccountGroups();
    refetchNotificationsForAccounts?.();
  }, []);

  useEffect(() => {
    if (accountPaginationMountRef.current) {
      accountPaginationMountRef.current = false;
      return;
    }
    const timer = setTimeout(() => refetchAccountList(), 300);
    return () => clearTimeout(timer);
  }, [accountPagination]);

  const openAccountsNotifPopover = (e) => {
    setAccountsNotifAnchorEl(e.currentTarget);
    refetchNotificationsForAccounts?.();
  };

  const closeAccountsNotifPopover = () => {
    setAccountsNotifAnchorEl(null);
  };

  const markAccountNotificationAsRead = async (notificationId) => {
    if (!notificationId) return;
    try {
      setMarkingReadId(notificationId);
      await axios.put(
        `${API_URL}/notification/${notificationId}/mark-as-read`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      refetchNotificationsForAccounts?.();
    } catch {
      toast.error("Failed to mark notification as read");
    } finally {
      setMarkingReadId(null);
    }
  };

  const handleClickAccountNotification = async (item) => {
    if (!item) return;
    if (!item?.read) {
      await markAccountNotificationAsRead(item.id);
    }
    const dest = getNotificationDestination(item);
    if (dest?.path) {
      navigate(dest.path, dest.state ? { state: dest.state } : undefined);
      closeAccountsNotifPopover();
    }
  };

  const fetchAccountGroups = async () => {
    try {
      const res = await axios.get(`${API_URL}/account-master-groups`);
      setAccountGroups(res?.data?.data || []);
    } catch {
      setAccountGroups([]);
    }
  };

  const refreshAccountsAndGroups = () => {
    refetchAccountList();
    fetchAccountGroups();
  };

  const groupMembershipByAccountId = useMemo(() => {
    const map = new Map();
    accountGroups.forEach((group) => {
      (group.members || []).forEach((member) => {
        if (member.accountMasterId) {
          map.set(member.accountMasterId, { id: group.id, name: group.name });
        }
      });
    });
    return map;
  }, [accountGroups]);

  const accountRows = useMemo(
    () =>
      (accountsList || []).map((account) => {
        const group = groupMembershipByAccountId.get(account.clientAccountId);
        return {
          ...account,
          groupId: group?.id ?? account.groupId ?? null,
          groupName: group?.name ?? account.groupName ?? null,
        };
      }),
    [accountsList, groupMembershipByAccountId]
  );

  const groupFilterOptions = useMemo(
    () => [
      { id: "all", name: "All groups" },
      { id: "ungrouped", name: "Ungrouped" },
      ...accountGroups.map((group) => ({ id: group.id, name: group.name })),
    ],
    [accountGroups]
  );

  const filteredAccountRows = useMemo(() => {
    if (!selectedGroupFilter || selectedGroupFilter.id === "all") return accountRows;
    if (selectedGroupFilter.id === "ungrouped") {
      return accountRows.filter((account) => !account.groupId);
    }
    return accountRows.filter(
      (account) => Number(account.groupId) === Number(selectedGroupFilter.id)
    );
  }, [accountRows, selectedGroupFilter]);

  const activeTab = accountTabOptions[value];
  const activeTabValue = activeTab?.value;

  useEffect(() => {
    if (activeTabValue === "provider" && !providerLoadDoneRef.current) {
      providerLoadDoneRef.current = true;
      refetchProvider();
    }
  }, [activeTabValue]);

  useEffect(() => {
    if (providerPaginationMountRef.current) {
      providerPaginationMountRef.current = false;
      return;
    }
    const timer = setTimeout(() => refetchProvider(), 300);
    return () => clearTimeout(timer);
  }, [providerPagination]);

  const recordsCount =
    activeTabValue === "client"
      ? filteredAccountRows?.length
      : activeTabValue === "provider"
      ? providerTotalItems
      : activeTabValue === "payment-verifications"
      ? paymentVerificationCount
      : activeTabValue === "verification"
      ? verificationCount
      : 0;

  const deleteAccount = () => {
    deleteData(`${API_URL}/accounts/${openConfirmationModal?.id}`, () => {
      setOpenConfirmationModal({ state: false, id: null });
      refetchAccountList();
    });
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
    const status = accountTabOptions[newValue];
    if (status?.value) {
      localStorage.setItem("accountTab", status.value);
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "Name",
        size: 150,
        accessorKey: "clientName",
        Cell: ({ row }) => {
          return row.original?.clientData?.passportNumber ? (
            <div className="capitalize">
              {row.original.clientName || "N/A"} - {row.original?.clientData?.passportNumber}
            </div>
          ) : (
            <div className="capitalize">{row.original.clientName || "N/A"}</div>
          );
        },
      },

      {
        accessorKey: "caseOfficer", //normal accessorKey
        header: "Case Officer",
        size: 150,
        Cell: ({ row }) => {
          return <div>{row.original.caseOfficer || "-"}</div>;
        },
      },
      {
        accessorKey: "groupName",
        header: "Group",
        size: 120,
        Cell: ({ row }) =>
          row.original.groupName ? (
            <Chip
              label={row.original.groupName}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontSize: 11 }}
            />
          ) : (
            <Chip label="Ungrouped" size="small" variant="outlined" sx={{ fontSize: 11 }} />
          ),
      },
      {
        accessorKey: "totalAgentCost", //normal accessorKey
        header: "Agent Cost",
        size: 50,
        Cell: ({ row }) => {
          return (
            <div>
              {currency} {row.original?.totalAgentCost?.toFixed(2) || 0}
            </div>
          );
        },
      },
      {
        // Named totalCost on the wire, but it is the paid total (master.totalAmount is posted as
        // the sum of paid; effective mode sums paid too) — the header says what it holds.
        accessorKey: "totalCost" || 0,
        header: "Paid",
        size: 50,
        Cell: ({ row }) => {
          return (
            <div>
              {currency} {row.original.totalCost?.toFixed(2) || 0}
            </div>
          );
        },
      },

      {
        accessorKey: "totalDueAmount", //normal accessorKey
        header: "Due",
        size: 50,
        Cell: ({ row }) => {
          return (
            <div>
              {currency} {row.original.totalDueAmount?.toFixed(2) || 0}
            </div>
          );
        },
      },
      {
        accessorKey: "totalProfit",
        header: "Profit/Loss",
        size: 150,
        Cell: ({ row }) => {
          return (
            <div className={row.original.totalProfit < 0 ? "!text-red-500" : "!text-green-500 "}>
              {currency} {row.original.totalProfit?.toFixed(2) || 0}
            </div>
          );
        },
      },
      {
        accessorKey: "Action",
        header: "Action",
        size: 150,
        Cell: ({ row }) => {
          const item = row.original;
          return (
            <div className="flex items-center">
              {/* <Tooltip title="Generate Invoice" arrow>
                <IconButton
                  onClick={() => {
                    setSelectedAccount(item);
                    setIsGenerateInvoiceOpen(true);
                  }}
                >
                  <FaFileInvoiceDollar className="text-primary cursor-pointer" />
                </IconButton>
              </Tooltip> */}
              <Tooltip title="Edit Course" arrow>
                <IconButton
                  onClick={() => {
                    // setWholeLoading(true);
                    navigate("/admin/account/add", {
                      state: {
                        item: {
                          ...item,
                          hasAccountDetails:
                            item.hasAccountDetails ?? Boolean(item.clientAccountId),
                        },
                      },
                    });
                  }}
                >
                  <AiFillEdit className="text-primary cursor-pointer" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Course" arrow>
                <IconButton
                  onClick={() => {
                    setOpenConfirmationModal({ state: true, id: item?.clientAccountId });
                  }}
                >
                  <AiFillDelete className="text-primary cursor-pointer" />
                </IconButton>
              </Tooltip>
            </div>
          );
        },
      },
    ],
    []
  );

  const providerColumns = useMemo(
    () => [
      {
        header: "Provider Name",
        size: 200,
        accessorKey: "universityName",
        Cell: ({ row }) => {
          const pending = row?.original?.pendingClaimCount;
          return (
            <div className="flex items-center gap-2">
              <span>{row?.original?.universityName || "N/A"}</span>
              {pending > 0 && (
                <Tooltip title={`${pending} term${pending === 1 ? "" : "s"} pending claim`} arrow>
                  <span className="inline-flex items-center justify-center rounded-full bg-amber-100 text-amber-800 text-xs font-semibold px-1.5 py-0.5 min-w-[20px]">
                    ⚠ {pending}
                  </span>
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "totalStudents",
        header: "Students",
        size: 90,
        Cell: ({ row }) => <div>{row?.original?.totalStudents ?? 0}</div>,
      },
      {
        accessorKey: "sentTermCount",
        header: "Terms",
        size: 80,
        Cell: ({ row }) => <div>{row?.original?.sentTermCount ?? 0}</div>,
      },
      {
        accessorKey: "totalCommission",
        header: "Commission",
        size: 190,
        Cell: ({ row }) => {
          const o = row?.original || {};
          return (
            <div>
              <div className="font-semibold">
                {currency} {Number(o.totalCommission || 0).toFixed(2)}
              </div>
              <div className="text-xs text-app-text-secondary">
                claimed {Number(o.claimedCommission || 0).toFixed(2)} · pending{" "}
                <span className={Number(o.pendingCommission) > 0 ? "text-amber-600" : ""}>
                  {Number(o.pendingCommission || 0).toFixed(2)}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "totalBonus",
        header: "Bonus",
        size: 190,
        Cell: ({ row }) => {
          const o = row?.original || {};
          return (
            <div>
              <div className="font-semibold">
                {currency} {Number(o.totalBonus || 0).toFixed(2)}
              </div>
              <div className="text-xs text-app-text-secondary">
                claimed {Number(o.claimedBonus || 0).toFixed(2)} · pending{" "}
                <span className={Number(o.pendingBonus) > 0 ? "text-amber-600" : ""}>
                  {Number(o.pendingBonus || 0).toFixed(2)}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "totalGST",
        header: "GST",
        size: 110,
        Cell: ({ row }) => (
          <div>
            {currency} {Number(row?.original?.totalGST || 0).toFixed(2)}
          </div>
        ),
      },
      {
        accessorKey: "Action",
        header: "Action",
        size: 150,
        Cell: ({ row }) => {
          const item = row.original;
          return (
            <div className="flex items-center">
              <Tooltip title="Edit Course" arrow>
                <IconButton
                  onClick={() =>
                    navigate("/admin/account/provider/add", {
                      state: { item },
                    })
                  }
                >
                  <AiFillEdit className="text-primary cursor-pointer" />
                </IconButton>
              </Tooltip>
            </div>
          );
        },
      },
    ],
    []
  );
  const providerTable = useMaterialReactTable({
    columns: providerColumns,
    data: providerList,
    enableRowNumbers: true,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    rowCount: providerTotalItems,
    onPaginationChange: (updater) => {
      setProviderPagination((prev) => {
        const next =
          typeof updater === "function"
            ? updater({ pageIndex: prev.pageIndex, pageSize: prev.pageSize })
            : updater;
        return { ...prev, pageIndex: next.pageIndex, pageSize: next.pageSize };
      });
    },
    onSortingChange: (updater) => {
      setProviderPagination((prev) => {
        const prevSorting = prev.sortBy
          ? [{ id: prev.sortBy, desc: prev.sortDirection === "DESC" }]
          : [];
        const next = typeof updater === "function" ? updater(prevSorting) : updater;
        const sort = next?.[0];
        return {
          ...prev,
          sortBy: sort?.id || "",
          sortDirection: sort?.desc ? "DESC" : "ASC",
          pageIndex: 0,
        };
      });
    },
    onGlobalFilterChange: (value) => {
      setProviderPagination((prev) => ({ ...prev, search: value ?? "", pageIndex: 0 }));
    },
    initialState: {
      density: "compact",
      showGlobalFilter: true,
    },
    state: {
      isLoading: isProviderListLoading,
      showProgressBars: isProviderListLoading,
      pagination: {
        pageIndex: providerPagination.pageIndex,
        pageSize: providerPagination.pageSize,
      },
      sorting: providerPagination.sortBy
        ? [{ id: providerPagination.sortBy, desc: providerPagination.sortDirection === "DESC" }]
        : [],
      globalFilter: providerPagination.search ?? "",
    },
    enableColumnPinning: true,
    ...getMrtTableThemeProps({ maxHeight: "68vh" }),
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        const isActionClick =
          event.target.closest(".MuiIconButton-root") || event.target.closest(".MuiPopover-root");
        if (!isActionClick) {
          navigate(`/admin/account/provider/add`, {
            state: {
              item: {
                ...row.original,
                hasAccountDetails:
                  row.original.hasAccountDetails ?? Boolean(row.original.clientAccountId),
              },
            },
          });
        }
      },
      sx: getMrtBodyRowSx(row),
    }),
  });

  const table = useMaterialReactTable({
    columns,
    data: filteredAccountRows,
    enableRowNumbers: true,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    rowCount: accountsTotalItems,
    onPaginationChange: (updater) => {
      setAccountPagination((prev) => {
        const next =
          typeof updater === "function"
            ? updater({ pageIndex: prev.pageIndex, pageSize: prev.pageSize })
            : updater;
        return { ...prev, pageIndex: next.pageIndex, pageSize: next.pageSize };
      });
    },
    onSortingChange: (updater) => {
      setAccountPagination((prev) => {
        const prevSorting = prev.sortBy
          ? [{ id: prev.sortBy, desc: prev.sortDirection === "DESC" }]
          : [];
        const next = typeof updater === "function" ? updater(prevSorting) : updater;
        const sort = next?.[0];
        return {
          ...prev,
          sortBy: sort?.id || "updatedDate",
          sortDirection: sort?.desc ? "DESC" : "ASC",
          pageIndex: 0,
        };
      });
    },
    onGlobalFilterChange: (value) => {
      setAccountPagination((prev) => ({ ...prev, search: value ?? "", pageIndex: 0 }));
    },
    initialState: {
      density: "compact",
      showGlobalFilter: true,
    },
    state: {
      isLoading: isAccountsListLoading,
      showProgressBars: isAccountsListLoading,
      pagination: {
        pageIndex: accountPagination.pageIndex,
        pageSize: accountPagination.pageSize,
      },
      sorting: accountPagination.sortBy
        ? [{ id: accountPagination.sortBy, desc: accountPagination.sortDirection === "DESC" }]
        : [],
      globalFilter: accountPagination.search ?? "",
    },
    enableColumnPinning: true,
    ...getMrtTableThemeProps({ maxHeight: "65vh" }),
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        const isActionClick =
          event.target.closest(".MuiIconButton-root") || event.target.closest(".MuiPopover-root");
        if (!isActionClick) {
          navigate(`/admin/account/add`, {
            state: { item: row.original },
          });
        }
      },
      sx: getMrtBodyRowSx(row),
    }),
    renderTopToolbarCustomActions: ({ table }) => (
      <div className="mb-3 flex items-center gap-3 flex-wrap">
        <div className="max-w-sm w-[200px]">
          <Autocomplete
            size="small"
            options={groupFilterOptions}
            value={selectedGroupFilter}
            onChange={(_, value) => setSelectedGroupFilter(value)}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
            renderInput={(params) => <TextField {...params} label="Filter by group" size="small" />}
          />
        </div>
        <Tooltip title="Effective: for Admission, only installments with term date on or before today count; Visa/RPL/etc. always use full totals. Paid = total received; Agent Cost = total module cost. Effective Due can exceed Total when future admission terms have credits excluded.">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={accountPagination?.effective ? "EFFECTIVE" : "TOTAL"}
            onChange={(_, v) => {
              if (v) {
                setAccountPagination((prev) => ({
                  ...prev,
                  effective: v === "EFFECTIVE",
                  pageIndex: 0,
                }));
              }
            }}
          >
            <ToggleButton value="TOTAL">Total</ToggleButton>
            <ToggleButton value="EFFECTIVE">Effective</ToggleButton>
          </ToggleButtonGroup>
        </Tooltip>
      </div>
    ),
  });

  const handleAddAccount = () => {
    setOpenAddAccountModal(true);
  };
  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full mb-2 px-4">
        <div className="relative flex flex-col min-w-0 break-words w-full shadow-lg rounded bg-app-surface text-app-text">
          <div className="rounded-t mb-0 px-4 py-4 border-b border-app-border">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <h3 className="font-semibold text-xl text-app-text">Account Details</h3>
                  <RefreshData
                    refreshData={() => {
                      if (value === 0) refreshAccountsAndGroups();
                      if (value === 1) refetchProvider();
                    }}
                  />{" "}
                  <Tooltip title="Account notifications">
                    <IconButton onClick={openAccountsNotifPopover}>
                      <Badge
                        color="error"
                        overlap="circular"
                        invisible={unreadAccountNotificationsCount === 0}
                        badgeContent={unreadAccountNotificationsCount}
                      >
                        <NotificationsNoneIcon />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                </div>
                <div className="flex items-center gap-3 text-sm text-app-text-secondary">
                  <div className="rounded-lg bg-app-surface-muted border border-app-border px-3 py-1.5">
                    <span className="font-semibold text-app-text">{recordsCount}</span> records
                  </div>
                  <div className="rounded-lg bg-app-surface-muted border border-app-border px-3 py-1.5">
                    Active tab:{" "}
                    <span className="font-semibold text-app-text">
                      {accountTabOptions[value]?.label || "Client"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <Button variant="contained" color="primary" onClick={handleAddAccount}>
                  Add Account
                </Button>
                {activeTabValue === "client" && (
                  <>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => setOpenGroupManager(true)}
                    >
                      Manage Groups
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => setOpenBulkProviderInvoice(true)}
                    >
                      Create Bulk Invoice
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>

          <Tabs
            value={value}
            onChange={handleChange}
            variant="scrollable"
            scrollButtons
            allowScrollButtonsMobile
            role="navigation"
            sx={getThemedTabsSx({
              "& .MuiTab-root": {
                minHeight: 52,
                fontSize: 15,
              },
            })}
          >
            {accountTabOptions.map((arg) => (
              <Tab
                label={arg?.label}
                key={arg?.label}
                sx={{
                  fontWeight: "bold",
                  fontSize: 15,
                }}
              />
            ))}
          </Tabs>
          <TabPanel value={value} index={value}>
            <div className="block w-full overflow-x-auto p-4">
              {activeTabValue === "client" ? (
                <>
                  <MaterialReactTable table={table} />
                </>
              ) : activeTabValue === "provider" ? (
                <div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                    {[
                      { label: "Total Commission", value: providerSummary.commission },
                      { label: "Total Bonus", value: providerSummary.bonus },
                      { label: "Total GST", value: providerSummary.gst },
                      { label: "Students", value: providerSummary.students, money: false },
                      {
                        label: "Pending Claims",
                        value: providerSummary.pendingClaims,
                        money: false,
                        color:
                          providerSummary.pendingClaims > 0 ? "text-amber-600" : "text-app-text",
                      },
                    ].map((c) => (
                      <div
                        key={c.label}
                        className="bg-app-surface rounded-lg shadow-sm border border-app-border p-3"
                      >
                        <div className="text-xs uppercase tracking-wide text-app-text-muted">
                          {c.label}
                        </div>
                        <div className={`text-lg font-bold ${c.color || "text-app-text"}`}>
                          {c.money === false
                            ? c.value
                            : `${currency} ${Number(c.value || 0).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}`}
                        </div>
                      </div>
                    ))}
                  </div>
                  <MaterialReactTable table={providerTable} />
                </div>
              ) : activeTabValue === "payment-verifications" ? (
                <ModulePaymentVerificationRequests onCountChange={setPaymentVerificationCount} />
              ) : activeTabValue === "verification" ? (
                <AccountVerificationRequests onCountChange={setVerificationCount} />
              ) : null}
            </div>
          </TabPanel>
        </div>
      </div>

      <AccountNotificationsPopover
        anchorEl={accountsNotifAnchorEl}
        open={Boolean(accountsNotifAnchorEl)}
        onClose={closeAccountsNotifPopover}
        notifications={accountNotifications}
        isLoading={isNotificationLoadingForAccounts}
        onRefresh={() => refetchNotificationsForAccounts?.()}
        onClickNotification={handleClickAccountNotification}
        markingReadId={markingReadId}
        readEnum={notificationPaginationForAccounts?.readEnum || "ALL"}
        onReadFilterChange={(readEnum) =>
          refetchNotificationDataForAccounts?.({ readEnum, pageIndex: 0 })
        }
        page={notificationPaginationForAccounts?.pageIndex || 0}
        totalPages={notificationsListForAccounts?.totalPages || 1}
        onPageChange={(pageIndex) =>
          refetchNotificationDataForAccounts?.({ pageIndex: Math.max(0, pageIndex) })
        }
      />

      {openAddAccountModal && (
        <AddAccountsModal
          open={openAddAccountModal}
          onClose={() => setOpenAddAccountModal(false)}
          accountsList={accountsList}
        />
      )}
      <BulkProviderInvoiceModal
        open={openBulkProviderInvoice}
        onClose={() => setOpenBulkProviderInvoice(false)}
        accountsList={accountsList}
        providerList={providerList}
      />
      <AccountMasterGroupManager
        open={openGroupManager}
        onClose={() => setOpenGroupManager(false)}
        groups={accountGroups}
        providerList={providerList}
        refetchProvider={refetchProvider}
        onRefresh={refreshAccountsAndGroups}
      />
      {openConfirmationModal.state && (
        <DeleteModal
          open={openConfirmationModal.state}
          item="Account Details"
          handleCancel={() => setOpenConfirmationModal({ state: false, id: null })}
          handleDelete={() => deleteAccount()}
        />
      )}

      {isGenerateInvoiceOpen && (
        <GenerateInvoice
          open={isGenerateInvoiceOpen}
          onClose={() => {
            setIsGenerateInvoiceOpen(false);
            setSelectedAccount(null);
          }}
          rowData={selectedAccount}
        />
      )}
    </div>
  );
};

export default Accounts;
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 0 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}
